import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface StatementActionData {
  type: 'statement' | 'instruction' | 'endCall';
  condition?: string | null;
  text: string;
}

export interface ResponseBranchData {
  condition: string;
  statusMappings: Record<string, string> | null;
  actions: (QuestionnaireScript | StatementActionData)[] | null;
}

export interface QuestionnaireScript {
  numericId?: number;
  questionID?: string;
  text: string;
  category: string;
  note?: string | null;
  responseBranches: ResponseBranchData[];
}

export interface QuestionnaireData {
  script: QuestionnaireScript[];
}

export interface AgentStudies {
  Id: number;
  Name: string;
  AgentId: number | null;
  StudyId: number;
  Priority: string;
  Enabled: boolean;
  Description: string;
  CreatedBy: string;
  CreatedAt: string;
  ModifiedBy: string;
  ModifiedAt: string;
}

export interface SaveQuestionnaireRequest {
  agent_id: number;
  questionnaire_data: QuestionnaireData;
  agent_studies: AgentStudies[];
  agent_details?: {
    name: string;
    desc: string;
  };
  created_by: string;
}

export interface SaveQuestionnaireResponse {
  id: number;
  agent_id: number;
  version: number;
  created_by: string;
  created_at: string;
}

export interface StartBuildRequest {
  agent_id: number;
  created_by: string;
}

export interface StartBuildResponse {
  success: boolean;
  message: string;
  build_id: number;
  build_version: number;
  questionnaire_version: number;
  status: string;
  agent_id: number;
}

export interface BuildArtifacts {
  questionnaire_txt?: string;
  rules_json?: any[];
  dnq_criteria?: any;
}

export interface BuildStatusResponse {
  Id: number;
  BuildVersion: number;
  Status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  BuildStartedAt: string;
  BuildCompletedAt: string | null;
  BuildArtifacts: BuildArtifacts | null;
  BuildLog: string | null;
}

export interface PublishBuildRequest {
  modified_by: string;
}

export interface PublishBuildResponse {
  success: boolean;
  message: string;
  agent_id: number;
  published_build_id: number;
  published_build_version: number;
}

export interface CategoriesResponse {
  success: boolean;
  categories: Array<string | { Id: number; Name: string }>;
}

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireService {

  private readonly baseUrl = `${environment.comforceURL}/questionnaires`;
  private readonly agentsBaseUrl = `${environment.comforceURL}/agents`;

  constructor(private http: HttpClient) { }

  saveQuestionnaire(payload: SaveQuestionnaireRequest): Observable<SaveQuestionnaireResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'accept': 'application/json'
    });

    return this.http.post<SaveQuestionnaireResponse>(`${this.baseUrl}/save`, payload, { headers });
  }

  startBuild(payload: StartBuildRequest): Observable<StartBuildResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'accept': 'application/json'
    });

    return this.http.post<StartBuildResponse>(`${this.baseUrl}/build/start`, payload, { headers });
  }

  getLatestBuildStatus(agentId: number): Observable<BuildStatusResponse> {
    const headers = new HttpHeaders({
      'accept': 'application/json'
    });

    return this.http.get<BuildStatusResponse>(`${this.agentsBaseUrl}/${agentId}/builds/latest`, { headers });
  }

  publishBuild(agentId: number, payload: PublishBuildRequest): Observable<PublishBuildResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'accept': 'application/json'
    });

    return this.http.post<PublishBuildResponse>(`${this.agentsBaseUrl}/${agentId}/builds/publish`, payload, { headers });
  }

  getCategories(): Observable<CategoriesResponse> {
    const headers = new HttpHeaders({
      'accept': 'application/json'
    });

    return this.http.get<CategoriesResponse>(`${this.baseUrl}/categories`, { headers });
  }
}
